
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Check, X, Trash2, Receipt } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useState } from "react";
import { PurchaseReceiptDialog } from "@/components/purchases/PurchaseReceiptDialog";

interface PurchaseRequestCardProps {
  request: any;
  onStatusUpdate: (requestId: string, status: string) => void;
  onViewDetails: (request: any) => void;
  onDelete: (requestId: string) => void;
  isUpdating: boolean;
  isDeleting: boolean;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
}

export const PurchaseRequestCard = ({
  request,
  onStatusUpdate,
  onViewDetails,
  onDelete,
  isUpdating,
  isDeleting,
  getStatusColor,
  getStatusText,
}: PurchaseRequestCardProps) => {
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);

  return (
    <>
      <Card className="p-4">
        <CardContent className="p-0">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h4 className="font-medium text-lg">{request.product?.title || 'Produit supprimé'}</h4>
              <p className="text-sm text-muted-foreground">
                Par {request.buyer?.full_name || 'Utilisateur inconnu'}
              </p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(request.created_at), "PPPp", { locale: fr })}
              </p>
            </div>
            <Badge className={getStatusColor(request.status)}>
              {getStatusText(request.status)}
            </Badge>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span>Quantité:</span>
              <span>{request.quantity}</span>
            </div>
            {request.product?.price && (
              <div className="flex justify-between text-sm">
                <span>Prix unitaire:</span>
                <span>{request.product.price}€</span>
              </div>
            )}
            {request.product?.price && (
              <div className="flex justify-between text-sm font-medium">
                <span>Total:</span>
                <span>{(request.product.price * request.quantity).toFixed(2)}€</span>
              </div>
            )}
            {request.pickup_address && (
              <div className="text-sm">
                <span className="font-medium">Adresse:</span>
                <p className="text-muted-foreground">{request.pickup_address}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(request)}
              className="flex items-center gap-1"
            >
              <Eye className="h-4 w-4" />
              Détails
            </Button>

            {request.status === 'pending' && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => onStatusUpdate(request.id, 'approved')}
                  disabled={isUpdating}
                  className="flex items-center gap-1"
                >
                  <Check className="h-4 w-4" />
                  Approuver
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onStatusUpdate(request.id, 'rejected')}
                  disabled={isUpdating}
                  className="flex items-center gap-1"
                >
                  <X className="h-4 w-4" />
                  Refuser
                </Button>
              </>
            )}

            {request.status === 'approved' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowReceiptDialog(true)}
                className="flex items-center gap-1"
              >
                <Receipt className="h-4 w-4" />
                Voir le reçu
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(request.id)}
              disabled={isDeleting}
              className="text-destructive hover:text-destructive flex items-center gap-1"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          </div>
        </CardContent>
      </Card>

      <PurchaseReceiptDialog
        open={showReceiptDialog}
        onOpenChange={setShowReceiptDialog}
        purchaseRequestId={request.id}
      />
    </>
  );
};
